import { useState } from "react";
import axios from "axios";
import { ArrowUpRight, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/site/Reveal";
import { CALENDLY_URL } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "Leadership Transition Programme (Cohort)",
  "1-2-1 Leadership Coaching",
  "Atlantis (Simulation)",
  "Combination / not sure yet",
  "Speaking & workshops",
];

const SIZES = [
  "< 250 employees",
  "250 – 1,000",
  "1,000 – 5,000",
  "5,000 – 25,000",
  "25,000+",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", company: "", role: "",
    organisation_size: "", interest: "From Expert to Leader (Cohort)", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiry`, form);
      setDone(true);
      toast.success("Enquiry sent. Pascale will be in touch personally.");
      setForm({ name: "", email: "", company: "", role: "", organisation_size: "", interest: INTERESTS[0], message: "" });
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send enquiry. Please try again or email hello@globalwomenascend.com");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="pt-36 lg:pt-44 pb-16 bg-gwa-bg">
        <div className="gwa-container grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-2">
            <Reveal><p className="gwa-eyebrow"><span className="gwa-rule" />Enquire</p></Reveal>
          </div>
          <div className="lg:col-span-10">
            <Reveal delay={100}>
              <h1 className="gwa-display text-5xl lg:text-7xl text-gwa-primary leading-[1.02]">
                Send a brief.<br />Or <em className="italic font-light text-gwa-purple">book a 30-minute call.</em>
              </h1>
            </Reveal>
            <Reveal delay={200}>
              <p className="mt-10 text-lg text-gwa-muted-fg max-w-3xl leading-relaxed">
                For corporate enquiries, the form below is the fastest route. For individuals exploring 1:1 executive coaching, book directly via the introductory call link.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-24 lg:pb-32 bg-gwa-bg">
        <div className="gwa-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Form */}
          <div className="lg:col-span-8">
            <Reveal>
              <div className="bg-gwa-ivory border border-gwa-border p-8 lg:p-12">
                <p className="gwa-eyebrow"><span className="gwa-rule" />Corporate enquiry</p>
                <h2 className="gwa-display text-3xl lg:text-4xl mt-5 text-gwa-primary leading-tight">Tell us the brief.</h2>
                <p className="mt-3 text-gwa-muted-fg">We respond personally within two working days.</p>

                {done ? (
                  <div data-testid="enquiry-success" className="mt-10 flex items-start gap-4 bg-gwa-bg border border-gwa-border p-6">
                    <CheckCircle2 className="w-6 h-6 text-gwa-accent shrink-0 mt-0.5" />
                    <div>
                      <p className="font-serif text-2xl text-gwa-primary">Enquiry received.</p>
                      <p className="mt-2 text-gwa-muted-fg">Pascale will be in touch personally within two working days. If urgent, email hello@globalwomenascend.com.</p>
                      <button
                        type="button"
                        onClick={() => setDone(false)}
                        data-testid="enquiry-send-another"
                        className="gwa-btn-ghost mt-6"
                      >
                        Send another
                      </button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={submit} className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="enquiry-form">
                    <Field label="Name" required>
                      <input
                        type="text"
                        required
                        value={form.name}
                        onChange={update("name")}
                        data-testid="enquiry-input-name"
                        className="gwa-input"
                      />
                    </Field>
                    <Field label="Work email" required>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={update("email")}
                        data-testid="enquiry-input-email"
                        className="gwa-input"
                      />
                    </Field>
                    <Field label="Company" required>
                      <input
                        type="text"
                        required
                        value={form.company}
                        onChange={update("company")}
                        data-testid="enquiry-input-company"
                        className="gwa-input"
                      />
                    </Field>
                    <Field label="Your role" required>
                      <input
                        type="text"
                        required
                        value={form.role}
                        onChange={update("role")}
                        data-testid="enquiry-input-role"
                        className="gwa-input"
                      />
                    </Field>
                    <Field label="Organisation size">
                      <select
                        value={form.organisation_size}
                        onChange={update("organisation_size")}
                        data-testid="enquiry-input-size"
                        className="gwa-input"
                      >
                        <option value="">Select…</option>
                        {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </Field>
                    <Field label="Area of interest" required>
                      <select
                        value={form.interest}
                        onChange={update("interest")}
                        data-testid="enquiry-input-interest"
                        className="gwa-input"
                        required
                      >
                        {INTERESTS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </Field>
                    <div className="sm:col-span-2">
                      <Field label="Brief / context" required>
                        <textarea
                          required
                          value={form.message}
                          onChange={update("message")}
                          rows={5}
                          data-testid="enquiry-input-message"
                          className="gwa-input resize-y"
                          placeholder="What is the situation? Population in scope? Any constraints we should know about?"
                        />
                      </Field>
                    </div>
                    <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-5 pt-2">
                      <p className="text-xs text-gwa-muted-fg flex items-center gap-2">
                        <AlertCircle className="w-3.5 h-3.5" />
                        We treat all enquiries as commercially confidential.
                      </p>
                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="enquiry-submit"
                        className="gwa-btn-primary disabled:opacity-60"
                      >
                        {submitting ? "Sending…" : "Send enquiry"} <Send className="w-4 h-4" />
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </Reveal>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-10">
            <Reveal delay={120}>
              <div className="bg-[#651A75] text-gwa-bg p-8 lg:p-10 gwa-noise relative">
                <div className="relative z-10">
                  <p className="gwa-eyebrow-light"><span className="gwa-rule" />Prefer to speak?</p>
                  <h3 className="font-serif text-2xl lg:text-3xl mt-5 leading-tight">Book a 30-minute introductory call.</h3>
                  <p className="mt-4 text-gwa-bg/80 leading-relaxed">
                    Choose a time that works for you. We'll discuss what you're seeing in your organisation and whether this is the right fit.
                  </p>
                  <a
                    href={CALENDLY_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="contact-cta-book-individual"
                    className="gwa-btn-primary mt-8 bg-gwa-bg text-gwa-primary border-gwa-bg hover:bg-gwa-accent hover:border-gwa-accent"
                  >
                    Book introductory call <ArrowUpRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </Reveal>

            <Reveal delay={200}>
              <div>
                <p className="gwa-eyebrow"><span className="gwa-rule" />Direct</p>
                <ul className="mt-5 space-y-3 text-gwa-primary">
                  <li>
                    <a className="gwa-link-underline" href="mailto:hello@globalwomenascend.com">hello@globalwomenascend.com</a>
                  </li>
                  <li className="text-gwa-muted-fg text-sm">London, United Kingdom</li>
                  <li className="text-gwa-muted-fg text-sm">Working with clients across the UK and Europe</li>
                </ul>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-xs uppercase tracking-[0.16em] font-semibold text-gwa-primary/80">
        {label}{required && <span className="text-gwa-accent"> *</span>}
      </span>
      {children}
    </label>
  );
}
